import React from 'react';

import {
  Switch,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@eluve/components';

/**
 * Shown when the operator tries to activate a bundle whose model args is
 * inactive. The DB trigger (ENG-4427) rejects that activation regardless, so
 * this is the UX-side explanation for why the toggle is disabled.
 */
export const INACTIVE_MODEL_ARGS_TOOLTIP =
  'Model args are inactive — reactivate them before activating this bundle.';

export interface BundleActiveToggleProps {
  /** Whether the bundle is currently active. */
  isActive: boolean;
  /** Whether the bundle's model args is active. */
  modelArgsActive: boolean;
  /** Fired when the operator turns the bundle on. */
  onActivate: () => void;
  /** Fired when the operator turns the bundle off. */
  onDeactivate: () => void;
  size?: 'sm' | 'default';
  /** Forwarded to the Switch so an external Label can associate via htmlFor. */
  id?: string;
}

/**
 * Active toggle for a template bundle. Turning a bundle OFF is always allowed;
 * turning it ON is blocked (disabled + tooltip) when its model args is inactive,
 * because no active bundle may reference inactive model args (enforced at the DB
 * level by the prevent_active_bundle_on_inactive_model_args trigger).
 */
export const BundleActiveToggle: React.FC<BundleActiveToggleProps> = ({
  isActive,
  modelArgsActive,
  onActivate,
  onDeactivate,
  size = 'sm',
  id,
}) => {
  const blocked = !isActive && !modelArgsActive;

  const toggle = (
    <Switch
      id={id}
      size={size}
      checked={isActive}
      disabled={blocked}
      aria-label="Active"
      onCheckedChange={(checked) => (checked ? onActivate() : onDeactivate())}
    />
  );

  if (!blocked) {
    return toggle;
  }

  // A disabled control emits no hover/focus events, so wrap it in a focusable
  // span that can host the tooltip.
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span tabIndex={0}>{toggle}</span>
      </TooltipTrigger>
      <TooltipContent>{INACTIVE_MODEL_ARGS_TOOLTIP}</TooltipContent>
    </Tooltip>
  );
};
